import {
  Component, ViewEncapsulation, ElementRef, ChangeDetectionStrategy, Input, Output,
  EventEmitter, ViewChild, Inject, ChangeDetectorRef, OnDestroy
} from '@angular/core';
import { Observable, of } from 'rxjs';
import isEmpty from 'lodash-es/isEmpty';

import { RANDOM_ID } from '@app/constants/general.constants';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { SearchType } from '@search-bar/entities/search-type';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { TOGGLE_SEARCH_PAGE_DELAY } from '@shared/base-search-bar/constants/search.constants';
import { locationNotFoundAmplitudePropsMapper } from '@app/modules/core/mappers/amplitude-mappers';
import {
  AutoCompleteDestination, AutoCompleteDestinationHotel, AutoCompleteDestinationLocation,
  HotelDestinationInputType, InputDestinationPlace
} from '@shared/base-search-bar/entities/autocomplete-destionation';
import { PlaceNamePipe } from '@app/pipes/place-name.pipe';
import { PlaceSelectors } from '@app/entities/place';
import { EventProperties, ProductProperties } from '@avo/interface/trigger-props';
import { isNullOrUndefined } from '@app/util/util';
import { BaseSearchInputAbstractComponent } from '@shared/base-search-bar/abstract/base-search-input.abstract';
import { InputTextSize } from '@design-system/components/molecules/input-text/entities/input-text.types';

@Component({
  selector: 'app-search-input-destination',
  templateUrl: './search-input-destination.component.html',
  styleUrls: ['./search-input-destination.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SearchInputDestinationComponent extends BaseSearchInputAbstractComponent implements OnDestroy {

  @Input() searchType: SearchType;
  @Input() place: InputDestinationPlace;
  @Input() placeType: PlaceType;
  @Input() countryCode: string;
  @Input() countryName: string;
  @Input() set autoCompletePlaces(autoCompletePlaces: AutoCompleteDestination) {
    this._autoCompletePlaces = autoCompletePlaces;
    setTimeout(() => this.sendNotFoundEvent(), TOGGLE_SEARCH_PAGE_DELAY);
  }
  @Input() placeExcept: AutocompletePlace;
  @Input() delay: number = 500;
  @Input() eventPath: EventTarget[];
  @Input() isMobile: boolean = false;
  @Input() autofocus: boolean = false;
  @Input() isLoadingAutocompletePlaces: boolean = false;
  @Input() isHome: boolean = false;

  @Output() selectDestination: EventEmitter<InputDestinationPlace> = new EventEmitter<InputDestinationPlace>();
  @Output() locationNotFoundEvent: EventEmitter<ProductProperties> = new EventEmitter<ProductProperties>();
  @Output() popularAmplitudeEvent: EventEmitter<EventProperties> = new EventEmitter<EventProperties>();
  @Output() selectSimpleDestination: EventEmitter<InputDestinationPlace> = new EventEmitter<InputDestinationPlace>();

  @ViewChild('destination') public destination: ElementRef;

  public showInput: boolean = false;
  public id: number = Math.floor(Math.random() * RANDOM_ID);
  public value: string = '';

  private _autoCompletePlaces: AutoCompleteDestination;

  constructor(
    @Inject(IS_BROWSER) public isBrowser: boolean,
    protected changeDetectorRef: ChangeDetectorRef,
    private placeNamePipe: PlaceNamePipe,
  ) {
    super(changeDetectorRef);
  }

  ngOnDestroy(): void {
    this.removeAutoCompletePlaces.emit();
  }

  get inputSize(): InputTextSize {
    return !this.isMobile && this.isResultsPage ? InputTextSize.MD : InputTextSize.RG;
  }

  get filled(): boolean {
    return !this.isMobile && !this.isResultsPage;
  }

  get staticFilled(): boolean {
    return !this.isMobile && this.isResultsPage;
  }
  
  get hotelDestinationLocationName$(): Observable<string> {
    if (!isEmpty(this.place)) {
      if (isEmpty(this.place.Name)) {
        return this.placeNamePipe.transform(this.place.NearestIata, PlaceSelectors.City);
      } else return of(this.place.Name);
    } else of(null);
  }

  get isResultsMarginTop(): boolean {
    return this.isResultsPage && this.isMobile;
  }

  get autoCompletePlaces(): AutoCompleteDestination {
    return this._autoCompletePlaces; 
  }

  get sendLocationNotFoundEvent(): boolean {
    return this.showInput && !!this.value && this.autoCompletePlaces?.hotels?.length === 0
      && this.autoCompletePlaces?.locations?.length === 0;
  }

  get destinationInputId(): string {
    return `destination-search-input-place-${this.placeType}`;
  }

  get activeDestinationInputId(): string {
    return `active-search-input-place-${this.placeType}`;
  }

  get destinationInfoHome(): boolean {
    return this.isHome && !this.isMobile;
  }

  get hasPlaceSelected(): boolean {
    return !isEmpty(this.place) && !!this.place.InputType && !isNullOrUndefined(this.place.Id);
  }

  get placeTypeText(): string {
    return this.placeType === PlaceType.DEPARTURE ? 'ORIGIN' : 'DESTINATION';
  }

  get isEmptyHotels(): boolean {
    return this.autoCompletePlaces.hotels.length === 0;
  }

  get isEmptyLocations(): boolean {
    return this.autoCompletePlaces.locations.length === 0;
  }

  get isResultsPage(): boolean {
    return this.searchType === SearchType.HOTELS_RESULTS;
  }

  get hotelDestinationInputType(): typeof HotelDestinationInputType {
    return HotelDestinationInputType;
  }

  public locationFullName(nameFull: string, name: string): string {
    if (name && nameFull?.toUpperCase().includes(`${name}, `.toUpperCase())) {
      return nameFull.slice(`${name}, `.length);
    } else return nameFull;
  }

  public placeItemId(placeId: string, place: SelectAutocompletePlace): string {
    return `option-place-cities-${place.code}-${placeId}`;
  }

  public emitSelectHotel(hotel: AutoCompleteDestinationHotel): void {
    this.selectDestination.emit({
      Id: hotel.Id,
      address: hotel.address,
      hotel_name: hotel.hotel_name,
      headline: hotel.headline,
      NearestIata: hotel.NearestIataCode,
      InputType: HotelDestinationInputType.hotel
    });
    this.toggleInput();
  }

  public emitSelectHotelLocation(location: AutoCompleteDestinationLocation): void {
    this.selectDestination.emit({
      Id: location.Id,
      Name: location.Name,
      NameFull: location.NameFull,
      Type: location.Type,
      NearestIata: location.nearestIataCode,
      InputType: HotelDestinationInputType.location
    });
    this.toggleInput();
  }

  public emitSelectSimplePlace(place: AutocompletePlace): void {
    this.selectSimpleDestination.emit({
      Id: 0,
      Name: place.description,
      NameFull: `${place.description}, ${place.country}`,
      Type: null,
      NearestIata: place.code,
      InputType: HotelDestinationInputType.location,
      isPopularSelection: true
    });
    this.toggleInput();
  }

  private sendNotFoundEvent(): void {
    if (this.sendLocationNotFoundEvent) {
      this.locationNotFoundEvent.emit(
        locationNotFoundAmplitudePropsMapper(this.searchType, this.value, this.destinationInputId));
    }
  }
}
